import { Modal } from '@/components/common/Modal'
import PDFViewer from '@/components/PDFViewer'
import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { fetchLabelPreviewBytes } from '@/features/order/generateShippingLabel'
import { Download, ExclamationCircle, Printer, Refresh } from 'flowbite-react-icons/outline'
import { useEffect, useState } from 'react'

import { isSafeLabelUrl } from './shippingLabelUrl'

/**
 * Discriminated modal state for the shipping-label preview.
 *
 * - ``loading`` — label request in flight.
 * - ``error``   — config gap, ``ok:false``, or network failure (honest: never a
 *   blank/fake preview). Optional :prop:`onRetry` renders a Retry action.
 * - ``ready``   — a real ``labelUrl`` was returned; renders the preview + Print
 *   + Download.
 * - ``idle``    — empty state (no label requested yet).
 */
export type ShippingLabelModalState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | {
      status: 'ready'
      labelUrl: string
      trackingNumber?: string
      labelFileType?: string
    }

export interface ShippingLabelModalProps {
  isOpen: boolean
  onClose: () => void
  state: ShippingLabelModalState
  /** When provided and the state is ``error``, renders a Retry button. */
  onRetry?: () => void
  /**
   * Authenticated provider-api client used to fetch the PDF label preview
   * through the SSRF-guarded proxy. Defaults to the singleton; injectable for
   * tests.
   */
  client?: ProviderAPIClient
}

/** PNG labels render as an <img>; everything else (PDF or unknown) uses the canvas PDFViewer. */
function isImageLabel(labelFileType: string | undefined): boolean {
  if (!labelFileType) return false
  const normalized = labelFileType.toUpperCase()
  return normalized === 'PNG' || normalized.startsWith('PNG') || normalized === 'JPEG' || normalized === 'JPG'
}

/**
 * Scheme allowlist for the label URL (see ``./shippingLabelUrl``) guards the
 * preview ``img`` ``src`` / anchor ``href`` sinks below.
 */
const PREVIEW_FRAME_CLASSES =
  'h-[calc(100vh-360px)] min-h-80 w-full rounded-base border border-border-base bg-white dark:border-gray-700 dark:bg-gray-900'

const UNSAFE_LABEL_ERROR = 'The generated shipping label is invalid or could not be displayed. Please try again.'
const PREVIEW_FETCH_ERROR = 'The shipping label preview could not be loaded.'

/**
 * Internal preview state for the proxied PDF bytes. ``idle`` before a fetch is
 * needed (non-PDF or not ready), ``loading`` while the proxy fetch is in
 * flight, ``ready`` with the fetched PDF bytes (rendered to a canvas by
 * ``PDFViewer`` — works inside the Epic embedded view where a nested ``blob:``
 * iframe does not), or ``error`` when the proxy failed (surface the error UI,
 * never a broken frame).
 */
type PdfPreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; bytes: Uint8Array }
  | { status: 'error' }

export function ShippingLabelModal({ isOpen, onClose, state, onRetry, client }: ShippingLabelModalProps) {
  // A `ready` PDF label is fetched through the authenticated proxy and rendered
  // as canvas pages via PDFViewer (Shippo's X-Frame-Options — and Epic's nested
  // iframe restrictions — block a direct/blob iframe preview).
  const isReadyPdf =
    state.status === 'ready' && isSafeLabelUrl(state.labelUrl) && !isImageLabel(state.labelFileType)
  const pdfLabelUrl = isReadyPdf ? state.labelUrl : null

  const [preview, setPreview] = useState<PdfPreviewState>({ status: 'idle' })

  useEffect(() => {
    // Only fetch a PDF preview for a ready PDF while the modal is open. When
    // there is nothing to preview (closed, non-PDF, unsafe url), reset to idle.
    if (!isOpen || !pdfLabelUrl) {
      setPreview({ status: 'idle' })
      return
    }

    let cancelled = false
    const activeClient = client ?? ProviderAPIClient.getInstance()
    setPreview({ status: 'loading' })

    fetchLabelPreviewBytes(activeClient, pdfLabelUrl).then((result) => {
      if (cancelled) return
      if (result.ok) {
        setPreview({ status: 'ready', bytes: result.bytes })
      } else {
        setPreview({ status: 'error' })
      }
    })

    return () => {
      cancelled = true
    }
    // pdfLabelUrl changes when a new label replaces the old one; re-fetch then.
  }, [isOpen, pdfLabelUrl, client])

  const handlePrint = () => {
    if (state.status !== 'ready' || !isSafeLabelUrl(state.labelUrl)) return
    // Open the RAW Shippo label in a new tab (never a blob): PDFs surface the
    // browser's native print preview, and the user can print an image tab the
    // same way. We never print the app chrome — only the label document.
    const printWindow = window.open(state.labelUrl, '_blank', 'noopener,noreferrer')
    printWindow?.focus()
  }

  // Defense-in-depth: a `ready` state whose labelUrl is empty/whitespace or has
  // a disallowed scheme is NOT an honest label. Render the error state (never a
  // blank/hijackable preview) even though the sole caller already rejects these.
  const readyButUnsafe = state.status === 'ready' && !isSafeLabelUrl(state.labelUrl)
  // A proxy fetch failure for a PDF preview is surfaced inline (an alert in
  // place of the frame) while KEEPING Download/Print usable — it is not the
  // top-level error state (which replaces the whole body and drops the actions).
  const previewFailed = isReadyPdf && preview.status === 'error'
  const showError = state.status === 'error' || readyButUnsafe
  const errorMessage = state.status === 'error' ? state.error : UNSAFE_LABEL_ERROR

  // While the proxied PDF preview is loading, show the spinner (not a blank frame).
  const showPreviewSpinner = state.status === 'loading' || (isReadyPdf && preview.status === 'loading')

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Shipping Label" size="lg">
      <div className="flex min-h-80 flex-col gap-4">
        {showPreviewSpinner && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
            <span
              className="h-8 w-8 animate-spin rounded-full border-2 border-border-base border-t-brand-600 dark:border-gray-700 dark:border-t-brand-500"
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-text-body dark:text-gray-400">Generating shipping label…</p>
          </div>
        )}

        {showError && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-error-50 text-error-600 dark:bg-gray-800 dark:text-error-400">
              <ExclamationCircle className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-semibold tracking-tight text-text-heading dark:text-white">
                We couldn’t generate the shipping label
              </h3>
              <p className="max-w-md text-sm text-text-body dark:text-gray-400" role="alert">
                {errorMessage}
              </p>
            </div>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex min-h-10 items-center gap-2 rounded-full bg-brand-600 px-5 py-2.5 text-sm font-medium text-white shadow-xs hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:bg-brand-600 dark:hover:bg-brand-700"
              >
                <Refresh className="h-4 w-4" aria-hidden="true" />
                Retry
              </button>
            )}
          </div>
        )}

        {state.status === 'idle' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center">
            <p className="text-sm font-medium text-text-body dark:text-gray-400">No shipping label to display yet.</p>
          </div>
        )}

        {state.status === 'ready' && !readyButUnsafe && !showPreviewSpinner && (
          <>
            {isImageLabel(state.labelFileType) ? (
              <img
                src={state.labelUrl}
                alt="Shipping label preview"
                className={`${PREVIEW_FRAME_CLASSES} object-contain`}
              />
            ) : previewFailed ? (
              <div
                className={`${PREVIEW_FRAME_CLASSES} flex flex-col items-center justify-center gap-3 p-6 text-center`}
              >
                <div className="flex size-12 items-center justify-center rounded-full bg-error-50 text-error-600 dark:bg-gray-800 dark:text-error-400">
                  <ExclamationCircle className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="max-w-md text-sm text-text-body dark:text-gray-400" role="alert">
                  {PREVIEW_FETCH_ERROR}
                </p>
              </div>
            ) : (
              preview.status === 'ready' && (
                <div
                  className={`${PREVIEW_FRAME_CLASSES} overflow-hidden`}
                  role="group"
                  aria-label="Shipping label preview"
                >
                  <PDFViewer
                    pdfBytes={preview.bytes}
                    filename="Shipping-Label.pdf"
                    className="h-full"
                    showToolbar={false}
                  />
                </div>
              )
            )}

            {state.trackingNumber && (
              <p className="text-sm text-text-body dark:text-gray-400">
                <span className="font-medium text-text-heading dark:text-white">Tracking number:</span>{' '}
                {state.trackingNumber}
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-3">
              <a
                href={state.labelUrl}
                download
                rel="noopener noreferrer"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-border-base bg-white px-5 py-2.5 text-sm font-medium text-text-heading shadow-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download
              </a>
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex min-h-10 items-center gap-2 rounded-full bg-brand-600 px-5 py-2.5 text-sm font-medium text-white shadow-xs hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:bg-brand-600 dark:hover:bg-brand-700"
              >
                <Printer className="h-4 w-4" aria-hidden="true" />
                Print
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
